import { useCallback, useEffect, useState } from 'react';
import { act } from '../lib/actions';
import { api } from '../lib/api';
import { useNova } from '../lib/store';
import { ConfirmButton, Drawer, Field, inputClass, Toggle, when } from './admin/kit';
import { Button, Panel } from './ui';

export type ReportKind = 'queues' | 'agents' | 'abandoned' | 'hourly' | 'outcomes' | 'sessions' | 'productivity' | 'handled';
type Period = 'yesterday' | 'last_7_days' | 'last_month';
type Frequency = 'daily' | 'weekly' | 'monthly';

export interface Schedule {
  id: string;
  name: string;
  report: ReportKind;
  period: Period;
  filters: { queueId?: string; agentId?: string; teamId?: string };
  frequency: Frequency;
  weekday: number | null;
  send_at: string;
  format: 'xlsx' | 'csv';
  recipients: string[];
  active: boolean;
  next_run_at: string;
  last_run_at: string | null;
  last_error: string | null;
}
export type Draft = Pick<Schedule, 'report' | 'filters'> & Partial<Schedule>;

const PERIODS: { id: Period; label: string; fits: Frequency }[] = [
  { id: 'yesterday', label: 'Yesterday', fits: 'daily' },
  { id: 'last_7_days', label: 'The last 7 days', fits: 'weekly' },
  { id: 'last_month', label: 'Last month', fits: 'monthly' },
];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function cadence(s: Pick<Schedule, 'frequency' | 'weekday' | 'send_at'>): string {
  if (s.frequency === 'daily') return `Every day at ${s.send_at}`;
  if (s.frequency === 'weekly') return `Every ${DAYS[s.weekday ?? 1]} at ${s.send_at}`;
  return `On the 1st of each month at ${s.send_at}`;
}

/** The organization's report emails (PRD RP-03), and the form to add or change one. */
export function ReportSchedules({ titles, editing, onEdit }: { titles: Record<ReportKind, string>; editing: Draft | null; onEdit: (d: Draft | null) => void }) {
  const [data, setData] = useState<{ emailConfigured: boolean; schedules: Schedule[] } | null>(null);
  const load = useCallback(() => {
    api<{ emailConfigured: boolean; schedules: Schedule[] }>('GET', '/scheduled-reports')
      .then(setData)
      .catch(() => setData(null));
  }, []);
  useEffect(load, [load]);

  async function sendNow(s: Schedule) {
    const r = await act(() => api<{ sentTo: string[] }>('POST', `/scheduled-reports/${s.id}/send`));
    if (r) useNova.getState().toast(`Sent to ${r.sentTo.join(', ')}`, 'success');
    load();
  }

  return (
    <Panel className="p-5">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-medium">Email schedules</h2>
        <p className="text-xs text-ink-3">Reports emailed as a spreadsheet, for complete days in your time zone.</p>
      </div>
      {data && !data.emailConfigured ? (
        <p className="mb-3 rounded-lg border border-wrapup/40 px-3 py-2 text-sm text-ink-2">
          Email is not set up on this server yet, so nothing is sent. Schedules you add here start going out once it is.
        </p>
      ) : null}
      {!data ? (
        <p className="text-sm text-ink-3">Loading…</p>
      ) : data.schedules.length === 0 ? (
        <p className="text-sm text-ink-2">No report emails yet. Pick a report above and choose Email this report.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-line">
          {data.schedules.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {s.name} {!s.active ? <span className="ml-1 text-xs text-ink-3">(paused)</span> : null}
                </p>
                <p className="text-sm text-ink-2">
                  {titles[s.report]}, {PERIODS.find((p) => p.id === s.period)?.label.toLowerCase()}, {s.format === 'csv' ? 'CSV' : 'Excel'}. {cadence(s)} to {s.recipients.join(', ')}
                </p>
                <p className={`text-xs ${s.last_error ? 'text-break' : 'text-ink-3'}`}>
                  {s.last_error ? `Last send failed: ${s.last_error}` : s.last_run_at ? `Last sent ${when(s.last_run_at)}` : 'Not sent yet'}
                  {s.active ? ` · next ${when(s.next_run_at)}` : ''}
                </p>
              </div>
              <Button tone="quiet" className="px-3 py-1.5 text-sm" disabled={!data.emailConfigured} onClick={() => void sendNow(s)}>
                Send now
              </Button>
              <Button tone="quiet" className="px-3 py-1.5 text-sm" onClick={() => onEdit(s)}>
                Edit
              </Button>
            </li>
          ))}
        </ul>
      )}
      {editing ? (
        <ScheduleForm
          draft={editing}
          titles={titles}
          onDone={() => {
            onEdit(null);
            load();
          }}
        />
      ) : null}
    </Panel>
  );
}

function ScheduleForm({ draft, titles, onDone }: { draft: Draft; titles: Record<ReportKind, string>; onDone: () => void }) {
  const [f, setF] = useState({
    name: draft.name ?? titles[draft.report],
    period: draft.period ?? ('yesterday' as Period),
    frequency: draft.frequency ?? ('daily' as Frequency),
    weekday: draft.weekday ?? 1,
    sendAt: draft.send_at ?? '07:00',
    format: draft.format ?? ('xlsx' as 'xlsx' | 'csv'),
    recipients: (draft.recipients ?? []).join(', '),
    active: draft.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const recipients = f.recipients
    .split(/[\s,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);

  async function save() {
    setBusy(true);
    const body = { ...f, recipients, report: draft.report, filters: draft.filters, weekday: f.frequency === 'weekly' ? f.weekday : null };
    const ok = await act(
      () => (draft.id ? api('PATCH', `/scheduled-reports/${draft.id}`, body) : api('POST', '/scheduled-reports', body)),
      draft.id ? 'Email schedule saved' : 'Report email scheduled',
    );
    setBusy(false);
    if (ok) onDone();
  }
  async function remove() {
    const ok = await act(() => api('DELETE', `/scheduled-reports/${draft.id}`).then(() => true), 'Email schedule deleted');
    if (ok) onDone();
  }

  return (
    <Drawer
      title={draft.id ? 'Edit report email' : `Email ${titles[draft.report].toLowerCase()}`}
      open
      onClose={onDone}
      footer={
        <>
          <Button tone="primary" className="px-4 py-2" disabled={busy || recipients.length === 0 || f.name.trim().length < 2} onClick={() => void save()}>
            {draft.id ? 'Save' : 'Schedule'}
          </Button>
          <Button tone="ghost" className="px-3 py-2" onClick={onDone}>
            Cancel
          </Button>
          {draft.id ? (
            <span className="ml-auto">
              <ConfirmButton confirmText="Delete it" onConfirm={() => void remove()}>
                Delete
              </ConfirmButton>
            </span>
          ) : null}
        </>
      }
    >
      <p className="mb-4 text-sm text-ink-2">
        {titles[draft.report]}
        {Object.keys(draft.filters).length ? ', with the filters chosen on the page' : ''}.
      </p>
      <Field label="Name" hint="The email's subject starts with it">
        {(id) => <input id={id} className={inputClass} value={f.name} onChange={(e) => set('name', e.target.value)} />}
      </Field>
      <Field label="How often">
        {(id) => (
          <select
            id={id}
            className={inputClass}
            value={f.frequency}
            onChange={(e) => {
              const frequency = e.target.value as Frequency;
              // Each cadence comes with the period it naturally covers; it can still be changed
              setF((x) => ({ ...x, frequency, period: PERIODS.find((p) => p.fits === frequency)!.id }));
            }}
          >
            <option value="daily">Every day</option>
            <option value="weekly">Every week</option>
            <option value="monthly">Every month, on the 1st</option>
          </select>
        )}
      </Field>
      {f.frequency === 'weekly' ? (
        <Field label="Day">
          {(id) => (
            <select id={id} className={inputClass} value={f.weekday} onChange={(e) => set('weekday', Number(e.target.value))}>
              {DAYS.map((d, i) => (
                <option key={d} value={i}>
                  {d}
                </option>
              ))}
            </select>
          )}
        </Field>
      ) : null}
      <Field label="Time" hint="In your organization's time zone">
        {(id) => <input id={id} type="time" className={`${inputClass} tabular`} value={f.sendAt} onChange={(e) => e.target.value && set('sendAt', e.target.value)} />}
      </Field>
      <Field label="Covering">
        {(id) => (
          <select id={id} className={inputClass} value={f.period} onChange={(e) => set('period', e.target.value as Period)}>
            {PERIODS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label="Format">
        {(id) => (
          <select id={id} className={inputClass} value={f.format} onChange={(e) => set('format', e.target.value as 'xlsx' | 'csv')}>
            <option value="xlsx">Excel</option>
            <option value="csv">CSV</option>
          </select>
        )}
      </Field>
      <Field label="Send to" hint="Email addresses, separated by commas. Up to 20.">
        {(id) => <textarea id={id} rows={2} className={inputClass} value={f.recipients} onChange={(e) => set('recipients', e.target.value)} />}
      </Field>
      <Toggle label="Sending" checked={f.active} onChange={(v) => set('active', v)} />
    </Drawer>
  );
}
